// AA — Shop the look. Loaded as a module by snippets/aa-look-panel.liquid.
//
// <aa-look>         one look: per-item size + add, offers line, Add all + total.
//                   Product page: on phones the pieces are collection product cards
//                   (their own quick add) and Add all opens a dialog; on desktop the
//                   dialog's list moves into the column instead.
//                   Look page (layout 'page'): pieces listed with size buttons and
//                   an Add each ([data-ref="add-one"]); Add all adds straight away.
// <aa-look-switch>  several looks, one shown at a time (PDP with more than one
//                   look, and the look page). Arrows, counter, ?look=.
// The homepage row of look tiles (sections/aa-looks-row.liquid) has no JS: each
// tile is a plain link to its look page.
//
// Adds go through /cart/add.js with the cart sections attached, wrapped in a
// CartLinesUpdateEvent (@shopify/events) — the same path product-form.js and the
// PDP upsell (aa-offer-upsell.js) use, so the drawer re-renders and opens as it
// does for Add to cart.
import { CartLinesUpdateEvent } from '@shopify/events';

const root = () => window.Shopify?.routes?.root || '/';

async function addItems(items, source, sourceElement) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((element) => element.dataset.sectionId)
    .filter(Boolean);

  const deferred = CartLinesUpdateEvent.createPromise();
  sourceElement.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity || 1 })),
      promise: deferred.promise,
    })
  );

  const response = await fetch(`${root()}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items, sections: sectionIds.join(',') }),
  });
  const json = await response.json();
  const cart = await (await fetch(`${root()}cart.js`)).json();
  const failed = !response.ok || Boolean(json.status);
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      items: cart.items,
      source,
      sourceId: sourceElement.id || source,
      itemCount: items.length,
      sections: failed ? undefined : json.sections,
      didError: failed,
    },
  });
  if (failed) throw new Error(json.description || json.message || 'Could not add to cart');
}

function track(name, look, count) {
  try {
    window.gtag?.('event', name, { aa_look: look, items: count });
  } catch (error) {}
  try {
    window.clarity?.('set', 'aa_look', look);
    window.clarity?.('event', name);
  } catch (error) {}
}

// Money in the visitor's currency, built from a sample the server formatted
// ({{ 123456 | money }}), so it follows the store's own money format per market.
function moneyFormatter(sample) {
  const match = sample && sample.match(/1([^\d]?)234([^\d])56/);
  if (!match) return (cents) => (cents / 100).toFixed(2);
  const [whole, thousands, decimal] = match;
  const [before, after] = sample.split(whole);
  return (cents) => {
    const units = Math.floor(cents / 100);
    const rest = String(cents % 100).padStart(2, '0');
    const int = String(units).replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return `${before}${int}${rest === '00' ? '' : decimal + rest}${after}`;
  };
}

const capitalise = (text) => text.charAt(0).toUpperCase() + text.slice(1);

class AALook extends HTMLElement {
  connectedCallback() {
    this.money = moneyFormatter(this.dataset.money);
    this.dialog = this.querySelector('[data-ref="dialog"]');

    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-ref="variant"]')) this.#onVariant();
    });
    this.addEventListener('click', (event) => this.#onClick(event));
    this.#placeList();
    this.#refresh();
    // The offer amounts need Shopify.currency.rate outside SGD; it is there by load.
    if (document.readyState !== 'complete') window.addEventListener('load', () => this.#refresh(), { once: true });
  }

  // Product page: the pieces list (sizes, +, Add all, offers) lives in the
  // Add all dialog on phones and in the column on desktop. One list, moved,
  // so a picked size carries over and nothing is ever added twice.
  #placeList() {
    const slot = this.querySelector('[data-ref="inline"]');
    const body = this.querySelector('[data-ref="qa-body"]');
    if (!slot || !body || !this.dialog) return;
    const desktop = window.matchMedia('(min-width: 750px)');
    const place = () => {
      if (desktop.matches) {
        if (this.dialog.open) this.dialog.close();
        if (body.parentElement !== slot) slot.append(body);
      } else if (body.parentElement !== this.dialog) {
        this.dialog.append(body);
      }
    };
    place();
    desktop.addEventListener('change', place);
  }

  get handle() {
    return this.dataset.look;
  }

  #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-ref="add-all"]')) {
      this.#error('');
      this.dialog?.showModal();
      return;
    }
    if (target.closest('[data-ref="qa-close"]') || target === this.dialog) {
      this.dialog?.close();
      return;
    }
    if (target.closest('[data-ref="confirm"]')) {
      this.#addAll();
      return;
    }
    const one = target.closest('[data-ref="add-one"]');
    if (one) {
      this.#addOne(one.closest('[data-item]'), one);
      return;
    }
    // Look page: "14 day exchanges" opens the accordion's Shipping & Returns
    // row and scrolls to it, as on the product page (blocks/aa-shipping-returns).
    const exchanges = target.closest('a[href="#shipping-returns"]');
    const details = exchanges && this.querySelector('#shipping-returns')?.closest('details');
    if (details) {
      event.preventDefault();
      details.open = true;
      details.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  #buyable() {
    return [...this.querySelectorAll('[data-item][data-available]')];
  }

  // A one-variant piece carries a hidden input; a sized piece the theme's size
  // buttons (radios), whose checked one is the pick.
  #line(item) {
    const input = item.querySelector('input[type="hidden"][data-ref="variant"], [data-ref="variant"]:checked');
    if (!input || input.disabled) return null;
    const properties = { _aa_look: this.handle };
    // Same line property as blocks/buy-buttons: the cart and order show the date.
    if (input.dataset.preorder) properties['Pre-order'] = input.dataset.preorder;
    return {
      id: Number(input.value),
      quantity: 1,
      price: Number(input.dataset.price) || 0,
      properties,
    };
  }

  #onVariant() {
    this.#refresh();
  }

  // Offer 2 is a free cap (snippets/aa-free-gift.liquid): once the rest of the
  // look reaches its threshold, the look's first cap is the free one. It reads
  // "$39 Free" here, drops out of the total, and Add all sends it as the gift
  // line (`_aa_gift`), so the cart keeps this cap rather than picking its own.
  #plan() {
    const entries = this.#buyable()
      .map((item) => ({ item, line: this.#line(item), cap: item.dataset.type === 'Cap' }))
      .filter((entry) => entry.line);
    const AAOffers = window.AAOffers;
    const bonus = AAOffers?.offers.find((offer) => offer.key === 'bonus' && offer.live);
    const min = bonus ? AAOffers.cents('bonus') : null;
    const rest = entries.filter((entry) => !entry.cap).reduce((sum, entry) => sum + entry.line.price, 0);
    const free = min !== null && rest >= min ? entries.find((entry) => entry.cap) : null;
    const full = entries.reduce((sum, entry) => sum + entry.line.price, 0);
    const total = full - (free ? free.line.price : 0);
    // A look without a cap that reaches the threshold on its own: the cart adds
    // the free cap, so the static free cap line shows (snippets/aa-look-free-cap).
    const freeCapLine = min !== null && !entries.some((entry) => entry.cap) && rest >= min;
    return { entries, free, total, full, freeCapLine };
  }

  #priceHtml(item, free) {
    const input = item.querySelector('input[type="hidden"][data-ref="variant"], [data-ref="variant"]:checked');
    const now = Number(input?.dataset.price) || 0;
    const compare = Number(input?.dataset.compare) || 0;
    if (free) return `<s class="aa-look__compare">${this.money(now)}</s> Free`;
    return (compare > now ? `<s class="aa-look__compare">${this.money(compare)}</s> ` : '') + this.money(now);
  }

  #refresh() {
    const { entries, free, total, full, freeCapLine } = this.#plan();
    this.querySelectorAll('[data-ref="free-cap"]').forEach((line) => (line.hidden = !freeCapLine));
    // Prices in the list (or the dialog's).
    this.#buyable().forEach((item) => {
      const row = item.querySelector('[data-ref="price"]');
      if (row) row.innerHTML = this.#priceHtml(item, free?.item === item);
    });
    // With the free cap, the button shows the look's full price struck through:
    // the look's own cap, or the cap the static free cap line stands for
    // (its price, in the shopper's currency, on data-price).
    const capLine = this.querySelector('[data-ref="free-cap"]');
    const lineCap = freeCapLine ? Number(capLine?.dataset.price) || 0 : 0;
    const was = full + lineCap;
    const totalHtml = !entries.length
      ? ''
      : (was > total ? `<s class="aa-look__total-was">${this.money(was)}</s> ` : '') + this.money(total);
    this.querySelectorAll('[data-ref="total"]').forEach((el) => (el.innerHTML = totalHtml));
    const all = this.querySelector('[data-ref="add-all"]');
    if (all) all.hidden = this.#buyable().length === 0;
    const confirm = this.querySelector('[data-ref="confirm"]');
    if (confirm) confirm.disabled = !entries.length;
    this.querySelectorAll('[data-ref="add-one"]').forEach((button) => {
      const item = button.closest('[data-item]');
      button.disabled = !item || !this.#line(item);
    });
    this.#offers(entries.length ? total : null);
  }

  #offers(total) {
    const boxes = [...this.querySelectorAll('[data-ref="offers"]')];
    const AAOffers = window.AAOffers;
    if (!boxes.length || !AAOffers) return;
    let html = '';
    if (total !== null) {
      // One line: "Get free shipping + free cap" for what the look reaches on
      // its own; the next offer it misses, if any, as "$12 more for free cap".
      const offers = AAOffers.offers
        .filter((offer) => offer.live && AAOffers.cents(offer.key) !== null)
        .sort((a, b) => AAOffers.cents(a.key) - AAOffers.cents(b.key));
      const bare = (reward) => String(reward).replace(/^(a|an)\s+/i, '');
      const reached = offers.filter((offer) => total >= AAOffers.cents(offer.key));
      const next = offers.find((offer) => total < AAOffers.cents(offer.key));
      const parts = [];
      if (reached.length) parts.push(`Get ${reached.map((offer) => bare(offer.reward)).join(' + ')}`);
      if (next) {
        const more = `${this.money(AAOffers.cents(next.key) - total)} more for ${bare(next.reward)}`;
        parts.push(reached.length ? more : capitalise(more));
      }
      if (parts.length) html = `<p class="aa-look__offer"${reached.length ? ' data-reached' : ''}>${parts.join(' · ')}</p>`;
    }
    boxes.forEach((box) => {
      box.innerHTML = html;
      box.hidden = !html;
    });
  }

  #error(message) {
    const error = this.querySelector('[data-ref="error"]');
    if (!error) return;
    error.textContent = message || '';
    error.hidden = !message;
  }

  // One listed piece, in the size picked in its row.
  async #addOne(item, button) {
    const line = item && this.#line(item);
    if (!line || button.disabled) return;
    const { price, ...entry } = line;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    this.#error('');
    try {
      await addItems([entry], 'aa-look', this);
      track('aa_look_add', this.handle, 1);
    } catch (error) {
      this.#error(error.message);
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  async #addAll() {
    const confirm = this.querySelector('[data-ref="confirm"]');
    const { entries, free } = this.#plan();
    const items = entries.map((entry) => {
      const { price, ...item } = entry.line;
      if (entry === free) item.properties = { ...item.properties, _aa_gift: '1' };
      return item;
    });
    if (!items.length) return;
    confirm?.setAttribute('aria-busy', 'true');
    this.#error('');
    try {
      // One free cap per order: a gift line the cart already picked makes way
      // for this look's cap.
      if (free) {
        const cart = await (await fetch(`${root()}cart.js`)).json();
        for (const line of cart.items.filter((line) => line.properties?._aa_gift)) {
          await fetch(`${root()}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: 0 }),
          });
        }
      }
      await addItems(items, 'aa-look', this);
      track('aa_look_add_all', this.handle, items.length);
      this.dialog?.close();
    } catch (error) {
      this.#error(error.message);
    } finally {
      confirm?.removeAttribute('aria-busy');
    }
  }

}

class AALookSwitch extends HTMLElement {
  connectedCallback() {
    this.panels = [...this.querySelectorAll('[data-look-panel]')];
    this.counter = this.querySelector('[data-ref="counter"]');
    this.index = Math.max(0, this.panels.findIndex((panel) => !panel.hidden));
    this.querySelector('[data-ref="prev"]')?.addEventListener('click', () => this.show(this.index - 1));
    this.querySelector('[data-ref="next"]')?.addEventListener('click', () => this.show(this.index + 1));
    this.addEventListener('keydown', (event) => {
      if (event.target.closest('select')) return;
      if (event.key === 'ArrowLeft') this.show(this.index - 1);
      if (event.key === 'ArrowRight') this.show(this.index + 1);
    });

    // Ad deep link, kept from the earlier section: ?look=<handle> opens that look
    // and scrolls to it; #look scrolls to the section.
    if (this.hasAttribute('data-deep-link')) {
      const wanted = new URLSearchParams(location.search).get('look');
      const at = wanted ? this.panels.findIndex((panel) => panel.dataset.lookPanel === wanted) : -1;
      if (at >= 0 || location.hash === '#look') {
        if (at > 0) this.show(at);
        const land = () => {
          const header = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 80;
          const page =
            (window.matchMedia('(min-width: 990px)').matches && document.querySelector('.page-wrapper')) ||
            document.scrollingElement;
          page.scrollTo({ top: this.getBoundingClientRect().top + page.scrollTop - header - 16 });
        };
        if (document.readyState === 'complete') requestAnimationFrame(land);
        else window.addEventListener('load', land, { once: true });
      }
    }
  }

  show(i) {
    const count = this.panels.length;
    if (!count) return;
    this.index = (i + count) % count;
    this.panels.forEach((panel, n) => (panel.hidden = n !== this.index));
    if (this.counter) this.counter.textContent = `${this.index + 1} / ${count}`;
  }
}

if (!customElements.get('aa-look')) customElements.define('aa-look', AALook);
if (!customElements.get('aa-look-switch')) customElements.define('aa-look-switch', AALookSwitch);
